import { useState } from "react"
import { useLocation } from "wouter"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import { 
  Camera, 
  CheckCircle, 
  X, 
  MessageSquare, 
  Clock,
  Mail,
  Upload,
  Share2,
  AlertCircle,
  Loader2,
  Pencil,
  Save
} from "lucide-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import type { ContentUpload } from "@shared/schema"

interface UploadReviewCardProps {
  upload: ContentUpload
}

const getUploadMethodIcon = (method: string) => {
  switch (method) {
    case "direct": return <Upload className="h-4 w-4" />
    case "email": return <Mail className="h-4 w-4" />
    case "token_share": return <Share2 className="h-4 w-4" />
    default: return <Camera className="h-4 w-4" />
  }
}

const getUploadMethodLabel = (method: string) => {
  switch (method) {
    case "direct": return "Direkter Upload"
    case "email": return "E-Mail Upload"
    case "token_share": return "Share-Link"
    default: return method
  }
}

export function UploadReviewCard({ upload }: UploadReviewCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [, setLocation] = useLocation()
  const [reviewNotes, setReviewNotes] = useState("")
  const [showRejectNotes, setShowRejectNotes] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)
  const [isEditingMessage, setIsEditingMessage] = useState(false)
  const [editedMessage, setEditedMessage] = useState(upload.message || "")

  const updateMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      return apiRequest('PATCH', `/api/uploads/${upload.id}/message`, { message })
    },
    onSuccess: () => {
      toast({
        title: "Nachricht gespeichert",
        description: "Der Text wurde erfolgreich aktualisiert."
      })
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] })
      setIsEditingMessage(false)
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Die Nachricht konnte nicht gespeichert werden.",
        variant: "destructive"
      })
    }
  })

  const handleSaveMessage = () => {
    updateMessageMutation.mutate(editedMessage.trim())
  }

  const handleCancelEdit = () => {
    setEditedMessage(upload.message || "")
    setIsEditingMessage(false)
  }

  const reviewMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: "approved" | "rejected", notes?: string }) => {
      return apiRequest('POST', `/api/uploads/${upload.id}/review`, { 
        status, 
        reviewNotes: notes 
      })
    },
    onSuccess: (data, variables) => {
      if (variables.status === "approved") {
        // Show A-Team toast after API succeeds
        toast({
          title: "Ihr A-Team ist am Start!",
          description: "Emma prüft Ihr Foto, Max schreibt Ihre Beiträge."
        })
        
        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] })
        queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-approved'] })
        
        // Auto-redirect to Fotobibliothek after 2 seconds
        setTimeout(() => {
          setLocation('/foto-bibliothek')
          setTimeout(() => {
            toast({
              title: "Ihre erstellten Beiträge",
              description: "Wählen Sie einen Beitrag aus und geben Sie ihn frei.",
            })
          }, 500)
        }, 2000)
      } else {
        toast({
          title: "Upload abgelehnt",
          description: "Upload wurde abgelehnt"
        })
        queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] })
        queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-approved'] })
      }
      
      setShowRejectNotes(false)
      setReviewNotes("")
    },
    onError: (error) => {
      toast({
        title: "Fehler beim Review",
        description: "Das Review konnte nicht verarbeitet werden.",
        variant: "destructive"
      })
    }
  })

  const handleApprove = () => {
    reviewMutation.mutate({ status: "approved" })
  }

  const handleReject = () => {
    if (showRejectNotes) {
      reviewMutation.mutate({ 
        status: "rejected", 
        notes: reviewNotes.trim() || "Kein Grund angegeben" 
      })
    } else {
      setShowRejectNotes(true)
    }
  }

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <Card className="hover-elevate">
      <CardContent className="p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-4">
          {/* Header with Photo Preview */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0">
              {/* Photo Preview with Loading/Error States */}
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-muted rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                {upload.filePath && !imageError ? (
                  <>
                    {imageLoading && (
                      <div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                      </div>
                    )}
                    <img 
                      src={upload.filePath} 
                      alt={upload.originalName}
                      className={`w-full h-full object-cover transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                      onLoad={() => setImageLoading(false)}
                      onError={() => {
                        setImageLoading(false);
                        setImageError(true);
                      }}
                    />
                  </>
                ) : (
                  <div className="w-full h-full bg-primary/10 rounded-lg flex items-center justify-center">
                    <Camera className="h-6 w-6 text-primary" />
                    {imageError && (
                      <div className="absolute inset-0 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="font-medium text-sm truncate" data-testid={`upload-name-${upload.id}`}>
                  {upload.originalName}
                </h4>
                <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  <span className="whitespace-nowrap">{formatDate(upload.createdAt!)}</span>
                  <span>•</span>
                  <span className="whitespace-nowrap">{formatFileSize(upload.fileSize)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-xs whitespace-nowrap">
                {getUploadMethodIcon(upload.uploadMethod)}
                <span className="ml-1 hidden sm:inline">{getUploadMethodLabel(upload.uploadMethod)}</span>
              </Badge>
            </div>
          </div>

          {/* Message Section - Always show with edit capability */}
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start space-x-2 flex-1 min-w-0">
                <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">Nachricht zum Foto:</p>
                  
                  {isEditingMessage ? (
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Beschreiben Sie Ihr Foto oder ergänzen Sie Kontext"
                        value={editedMessage}
                        onChange={(e) => setEditedMessage(e.target.value)}
                        className="min-h-[80px] resize-none text-sm"
                        data-testid={`textarea-edit-message-${upload.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveMessage}
                          disabled={updateMessageMutation.isPending}
                          data-testid={`button-save-message-${upload.id}`}
                        >
                          {updateMessageMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 mr-1" />
                          )}
                          Speichern
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={updateMessageMutation.isPending}
                          data-testid={`button-cancel-edit-${upload.id}`}
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {upload.message ? (
                        <p className="text-sm text-muted-foreground" data-testid={`upload-message-${upload.id}`}>
                          "{upload.message}"
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic" data-testid={`upload-message-${upload.id}`}>
                          Keine Nachricht vorhanden
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {!isEditingMessage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditingMessage(true)}
                  className="flex-shrink-0"
                  data-testid={`button-edit-message-${upload.id}`}
                >
                  <Pencil className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Bearbeiten</span>
                </Button>
              )}
            </div>
          </div>

          {/* Reject Notes Input */}
          {showRejectNotes && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Grund für Ablehnung (optional):
              </label>
              <Textarea
                placeholder="Warum wird dieses Foto abgelehnt?"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="min-h-[80px] resize-none"
                data-testid={`reject-notes-${upload.id}`}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
            <div className="text-xs text-muted-foreground flex items-center gap-1 order-2 sm:order-1">
              {upload.uploadMethod === "token_share" && (
                <>
                  <Share2 className="h-3 w-3 flex-shrink-0" />
                  <span className="hidden sm:inline">Via Share-Link hochgeladen</span>
                  <span className="sm:hidden">Share-Link</span>
                </>
              )}
              {upload.uploadMethod === "email" && (
                <>
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="hidden sm:inline">Via E-Mail hochgeladen</span>
                  <span className="sm:hidden">E-Mail</span>
                </>
              )}  
              {upload.uploadMethod === "direct" && (
                <>
                  <Upload className="h-3 w-3 flex-shrink-0" />
                  <span className="hidden sm:inline">Direkt hochgeladen</span>
                  <span className="sm:hidden">Direkt</span>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-end">
              {showRejectNotes && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowRejectNotes(false)
                    setReviewNotes("")
                  }}
                  data-testid={`button-cancel-reject-${upload.id}`}
                  className="flex-1 sm:flex-initial"
                >
                  Abbrechen
                </Button>
              )}
              
              <Button
                variant="destructive"
                size="sm"
                onClick={handleReject}
                disabled={reviewMutation.isPending}
                data-testid={`button-reject-${upload.id}`}
                className="flex-1 sm:flex-initial"
              >
                <X className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Ablehnen</span>
              </Button>
              
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={reviewMutation.isPending || showRejectNotes}
                data-testid={`button-approve-${upload.id}`}
                className="flex-1 sm:flex-initial"
              >
                {reviewMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:mr-1 animate-spin" />
                    <span className="hidden sm:inline">Wird freigegeben...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Freigeben</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}